import React, {
  useEffect, useRef, useState,
} from 'react';

import {
  useConfigStore, useAppStore, useMapStateStore, useMapDataStore, useMapInteractionStore,
} from '../../../stores';
import {
  getActiveCenter,
  getInitialZoom,
  getLatLngFromFocusXYZ,
  animateMapNavigation,
} from './GoogleMapHelpers';
import { applyMapStyle } from './GoogleMapTypesHelpers';
import { addGoogleMapCommPin } from './GoogleMapCommPin';

// eslint-disable-next-line import/no-cycle
import GoogleMapOverlayViewContainer from './GoogleMapOverlayViewContainer';

import './GoogleMap.scss';

// Green tints for multiple external location labels/routes, derived from $defaultCopy (#495a49).
// Anchor positions to try when resolving label overlaps.
const ANCHOR_POSITIONS = ['bottom', 'top', 'right', 'left'];

/**
 * Returns the bounding rect of a label element relative to the map container.
 */
function getLabelRect(labelEl) {
  const rect = labelEl.getBoundingClientRect();
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}

/**
 * Checks if two rects overlap.
 */
function rectsOverlap(a, b) {
  return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

/**
 * Resolves overlapping external location labels by cycling through anchor
 * positions (bottom, top, right, left) until no overlap exists.
 */
function resolveExternalLabelOverlaps(overlays) {
  const labels = overlays
    .map((o) => o.labelEl)
    .filter(Boolean);

  if (labels.length < 2) return;

  // For each label (except the first), check if it overlaps any earlier label.
  for (let i = 1; i < labels.length; i++) {
    const label = labels[i];
    let currentRect = getLabelRect(label);
    let hasOverlap = false;

    for (let j = 0; j < i; j++) {
      if (rectsOverlap(currentRect, getLabelRect(labels[j]))) {
        hasOverlap = true;
        break;
      }
    }

    if (!hasOverlap) continue;

    // Try each anchor position until overlap is resolved.
    for (let a = 0; a < ANCHOR_POSITIONS.length; a++) {
      const anchor = ANCHOR_POSITIONS[a];
      // Remove previous anchor class.
      ANCHOR_POSITIONS.forEach((pos) => {
        label.classList.remove(`externalLocationLabel__label--${pos}`);
      });
      if (anchor !== 'bottom') {
        label.classList.add(`externalLocationLabel__label--${anchor}`);
      }

      currentRect = getLabelRect(label);
      hasOverlap = false;
      for (let j = 0; j < i; j++) {
        if (rectsOverlap(currentRect, getLabelRect(labels[j]))) {
          hasOverlap = true;
          break;
        }
      }
      if (!hasOverlap) break;
    }
  }
}

const LOCATION_TINTS = [
  '#495a49', // original
  '#5e7a5e', // lighter
  '#738f73', // lighter still
  '#3d4d3d', // darker
  '#2f3d2f', // darkest
];

const GoogleMap = React.memo(({ initialFocusId }) => {
  const [commPinLatLng, setCommPinLatLng] = useState(null);
  const [initialCenterLatLng, setInitialCenterLatLng] = useState(null);
  const [commBounds, setCommBounds] = useState(null);
  const [mapOptions, setMapOptions] = useState(null);
  const [initialZoom, setInitialZoom] = useState(null);
  // Track if map is fully initialized (to prevent focus during init)
  const [isMapReady, setIsMapReady] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(
    () => !!window.google?.maps?.Map,
  );

  const isMountedRef = useRef(true);
  const googleMapContainer = useRef();
  const mapInstanceRef = useRef(null); // Track map instance for cleanup
  const setMapRef = useRef(null); // Track setMap for cleanup
  // Track programmatic zoom to avoid clearing focus on our own zoom changes
  const isProgrammaticZoomRef = useRef(false);
  // Track if initial focus has been applied (to avoid scroll lock on subsequent focus changes)
  const hasInitialFocusRef = useRef(false);

  // Map interaction store
  const focusLatLng = useMapInteractionStore((state) => state.focusLatLng);
  const focusXYZ = useMapInteractionStore((state) => state.focusXYZ);
  const setMap = useMapInteractionStore((state) => state.setMap);
  setMapRef.current = setMap; // Keep ref in sync for cleanup
  const map = useMapInteractionStore((state) => state.map);
  const overlayBounds = useMapInteractionStore((state) => state.overlayBounds);
  const setFocusLatLng = useMapInteractionStore((state) => state.setFocusLatLng);
  const setFocusXYZ = useMapInteractionStore((state) => state.setFocusXYZ);
  const setMapIdle = useMapInteractionStore((state) => state.setMapIdle);
  const setIsZooming = useMapInteractionStore((state) => state.setIsZooming);
  const externalLocations = useMapInteractionStore((state) => state.externalLocations);
  const setExternalLocations = useMapInteractionStore((state) => state.setExternalLocations);
  const externalMarker = useMapInteractionStore((state) => state.externalMarker);
  const setExternalMarker = useMapInteractionStore((state) => state.setExternalMarker);
  const externalMarkers = useMapInteractionStore((state) => state.externalMarkers);
  const setExternalMarkers = useMapInteractionStore((state) => state.setExternalMarkers);
  const isExternalFocus = useMapInteractionStore((state) => state.isExternalFocus);
  const setIsExternalFocus = useMapInteractionStore((state) => state.setIsExternalFocus);
  const routeRenderer = useMapInteractionStore((state) => state.routeRenderer);
  const setRouteRenderer = useMapInteractionStore((state) => state.setRouteRenderer);
  const routeRenderers = useMapInteractionStore((state) => state.routeRenderers);
  const setRouteRenderers = useMapInteractionStore((state) => state.setRouteRenderers);
  const travelTimeOverlay = useMapInteractionStore((state) => state.travelTimeOverlay);
  const setTravelTimeOverlay = useMapInteractionStore((state) => state.setTravelTimeOverlay);
  const travelTimeOverlays = useMapInteractionStore((state) => state.travelTimeOverlays);
  const setTravelTimeOverlays = useMapInteractionStore((state) => state.setTravelTimeOverlays);

  // Zustand stores
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const config = useConfigStore((state) => state.config);
  const settings = useAppStore((state) => state.settings);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const setActiveZoom = useMapStateStore((state) => state.setActiveZoom);
  const setEffectiveMaxZoom = useMapStateStore((state) => state.setEffectiveMaxZoom);
  const activeMapType = useMapStateStore((state) => state.activeMapType);
  const POIVisible = useMapStateStore((state) => state.POIVisible);
  const resetMap = useMapStateStore((state) => state.resetMap);
  const setResetMap = useMapStateStore((state) => state.setResetMap);
  const setSvgFocusId = useMapStateStore((state) => state.setSvgFocusId);
  const hasPendingResidenceFocus = useMapStateStore((state) => state.hasPendingResidenceFocus);
  const focusAnimationComplete = useMapStateStore((state) => state.focusAnimationComplete);
  const setFocusAnimationComplete = useMapStateStore((state) => state.setFocusAnimationComplete);
  const setHasPendingResidenceFocus = useMapStateStore(
    (state) => state.setHasPendingResidenceFocus,
  );
  const svgData = useMapDataStore((state) => state.svgData);

  const { googleMapsConfig } = mapConfig || {};

  const { platform } = settings || {};
  // SiteSee and PibPage start at zoom zone level; Interactive Map fits to bounds
  const isSiteSeeOrPib = platform === 'siteSee' || platform === 'page';
  // For PIB residence detail with focus, use enableLevels zoom as initial zoom
  const hasFocusedResidence = !!initialFocusId;

  const {
    maxZoomBounds,
    zooms,
    campusBounds,
    siteSee,
  } = googleMapsConfig || {};

  const {
    min: minZoom,
    max: maxZoom,
    initial: configInitialZoom,
    enableLevels: mapZoomToEnableLevels,
  } = zooms || {};

  const {
    center: siteSeeCenter,
  } = siteSee || {};

  // SiteSee/PibPage use enableLevels - 1; Interactive Map uses initial zoom (or enableLevels for focused residence)
  const effectiveInitialZoom = hasFocusedResidence
    ? mapZoomToEnableLevels
    : (isSiteSeeOrPib ? mapZoomToEnableLevels - 1 : configInitialZoom);

  const fitAndPanToCommBounds = () => {
    if (map && commBounds) {
      // Use Google's fitBounds which handles all the math correctly
      const bounds = new window.google.maps.LatLngBounds(
        { lat: commBounds.south, lng: commBounds.west }, // SW corner
        { lat: commBounds.north, lng: commBounds.east }, // NE corner
      );

      // fitBounds with no padding - let Google handle centering
      map.fitBounds(bounds, 0);
    }
  };

  // Poll for Google Maps API readiness
  useEffect(() => {
    if (googleMapsLoaded) return undefined;
    const interval = setInterval(() => {
      if (window.google?.maps?.Map) {
        setGoogleMapsLoaded(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [googleMapsLoaded]);

  // Cleanup mounted ref on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Prevent focus within the map container from causing scroll issues
  // This catches any focus event on map elements and immediately blurs them
  // The key insight: when Google Maps grabs focus, browsers scroll to show the element.
  // By blurring with preventScroll and restoring position, we prevent this behavior.
  useEffect(() => {
    const container = googleMapContainer.current;
    if (!container) return undefined;

    const handleFocusIn = ({ target }) => {
      // Only blur elements that are Google Maps internal elements
      // (not our custom controls which use e.currentTarget.blur() manually)
      if (target && target !== container) {
        // Check if it's a Google Maps internal element
        const isGoogleMapsElement = target.closest('.gm-style')
          || target.tagName === 'CANVAS'
          || target.tagName === 'IFRAME';

        if (isGoogleMapsElement) {
          // Capture scroll position BEFORE blurring
          // (browser may have already scrolled, but we capture for restoration)
          const { scrollY } = window;
          const { scrollX } = window;

          // Blur with preventScroll to prevent additional scroll
          target.blur({ preventScroll: true });

          // Restore scroll position on next frame in case browser scrolled
          requestAnimationFrame(() => {
            // Only restore if scroll position changed significantly (more than a few pixels)
            // This avoids fighting with intentional user scrolls
            const scrollDiff = Math.abs(window.scrollY - scrollY) + Math.abs(window.scrollX - scrollX);
            if (scrollDiff > 50) {
              window.scrollTo(scrollX, scrollY);
            }
          });
        }
      }
    };

    // Add listener in capture phase to catch focus as early as possible
    container.addEventListener('focusin', handleFocusIn, true);

    return () => {
      container.removeEventListener('focusin', handleFocusIn, true);
    };
  }, []);

  // Set initial focus when provided via props (e.g., for PIB residence pages).
  // Reacts to initialFocusId changes so PIB residence switches reuse the same
  // google.maps.Map instance instead of forcing a remount (avoids a billed
  // Dynamic Maps load per switch).
  useEffect(() => {
    if (initialFocusId) {
      setSvgFocusId(initialFocusId);
      setHasPendingResidenceFocus(true);
    }
    return () => {
      if (initialFocusId) {
        setSvgFocusId(null);
        setHasPendingResidenceFocus(false);
      }
    };
  }, [initialFocusId, setSvgFocusId, setHasPendingResidenceFocus]);

  // Set the visual bounds of the community.
  // campusBounds is pre-validated in API - null if any value missing.
  useEffect(() => {
    setCommBounds(campusBounds);
  }, [campusBounds]);

  // Set initial center lat lng.
  // SiteSee/PibPage use siteSeeCenter; Interactive Map uses commPinLatLng (campusCenter).
  useEffect(() => {
    const rawInitialCenterLatLng = getActiveCenter(
      isSiteSeeOrPib,
      siteSeeCenter,
      commPinLatLng,
    );
    setInitialCenterLatLng(rawInitialCenterLatLng);
  }, [isSiteSeeOrPib,
    siteSeeCenter,
    commPinLatLng]);

  // Set initial zoom.
  // SiteSee uses enableZoomZones; Interactive Map/PibPage use initial zoom (or enableLevels for focused residence).
  useEffect(() => {
    if (effectiveInitialZoom) {
      setInitialZoom(effectiveInitialZoom);
    }
  }, [effectiveInitialZoom]);

  // Assemble map options.
  // maxZoomBounds is pre-validated in API - null if any value missing.
  useEffect(() => {
    const rawMapOptions = {
      center: initialCenterLatLng,
      zoom: initialZoom,
      minZoom,
      maxZoom,
      disableDefaultUI: true,
      clickableIcons: false,
    };
    if (maxZoomBounds) {
      rawMapOptions.restriction = {
        latLngBounds: maxZoomBounds,
        strictBounds: false,
      };
    }
    setMapOptions(rawMapOptions);
  }, [initialCenterLatLng,
    initialZoom,
    minZoom,
    maxZoom,
    maxZoomBounds]);

  // Load Google Maps API and create map with mount checks
  // Only create once - don't recreate when mapOptions changes
  const hasCreatedMapRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (hasCreatedMapRef.current) {
      return;
    }

    if (!googleMapsLoaded) {
      return;
    }

    if (mapOptions && isMountedRef.current && googleMapContainer.current) {
      const rawMap = new window.google.maps.Map(
        googleMapContainer.current,
        mapOptions,
      );

      // Store in ref for cleanup
      mapInstanceRef.current = rawMap;
      hasCreatedMapRef.current = true;

      // Only sync initial zoom to store if NOT fitting to bounds
      // (fitBounds will set the zoom after it completes)
      if (isSiteSeeOrPib || !campusBounds) {
        const mapZoom = rawMap.getZoom();
        if (mapZoom) {
          setActiveZoom(mapZoom);
        }
      }

      setMap(rawMap);

      // Mark map as ready after a short delay to allow Google Maps to finish initializing
      // This prevents focus-grabbing during the initialization phase
      setTimeout(() => {
        if (isMountedRef.current) {
          setIsMapReady(true);
        }
      }, 300);
    }
  }, [googleMapsLoaded, mapOptions, setMap, setActiveZoom]);

  // Cleanup: only runs when component unmounts
  useEffect(() => () => {
    if (mapInstanceRef.current) {
      // Clear all event listeners from the map
      window.google?.maps?.event?.clearInstanceListeners(mapInstanceRef.current);
      mapInstanceRef.current = null;
    }
    // Clear the map reference from the store so next mount starts fresh
    if (setMapRef.current) {
      setMapRef.current(null);
    }
  }, []);

  // At load, fit and pan to campus bounds for Interactive Map and PibPage (not SiteSee).
  // Skip fitBounds if there's a pending residence focus (PIB residence page with focused unit).
  // Also skip if we've already done initial focus (to prevent fitBounds during residence switch).
  useEffect(() => {
    // Skip if we've already set up focus - prevents fitBounds during residence switches
    if (hasInitialFocusRef.current) {
      return;
    }

    if (map && hasPendingResidenceFocus && mapZoomToEnableLevels) {
      // For pending residence focus (PIB residence detail), use mapZoomToEnableLevels
      setActiveZoom(mapZoomToEnableLevels);
      hasInitialFocusRef.current = true;
    } else if (map && commBounds && !isSiteSeeOrPib) {
      // Interactive Map: fit to campus bounds
      fitAndPanToCommBounds();
      // Sync the resulting zoom to activeZoom after fitBounds completes
      const idleListener = map.addListener('idle', () => {
        const newZoom = map.getZoom();
        if (newZoom) {
          setActiveZoom(newZoom);
        }
        window.google?.maps?.event?.removeListener(idleListener);
      });
      hasInitialFocusRef.current = true;
    } else if (map && effectiveInitialZoom && initialCenterLatLng) {
      // SiteSee/PibPage or no bounds: use the effective initial zoom and center
      // Set both zoom and center since config may have loaded after map creation
      map.setZoom(effectiveInitialZoom);
      map.panTo(initialCenterLatLng);
      setActiveZoom(effectiveInitialZoom);
      hasInitialFocusRef.current = true;
    }
  }, [map,
    commBounds,
    isSiteSeeOrPib,
    effectiveInitialZoom,
    hasPendingResidenceFocus,
    mapZoomToEnableLevels,
    initialCenterLatLng]);

  // Toggle map style based on map type and POI visibility.
  // Using our own var for map type so we can set it from other components.
  useEffect(() => {
    if (map) {
      map.overlayMapTypes.clear();
      if (activeMapType === 'hybrid') {
        applyMapStyle(map, POIVisible ? 'HYBRID_POI_ON' : 'HYBRID_POI_OFF');
      } else {
        // roadmap
        applyMapStyle(map, POIVisible ? 'ROADMAP_POI_ON' : 'ROADMAP_POI_OFF');
      }
    }
  }, [map,
    POIVisible,
    activeMapType]);

  // Add community pin.
  addGoogleMapCommPin(commPinLatLng, setCommPinLatLng);

  // Reset map to initial zoom and center from other components.
  useEffect(() => {
    if (resetMap) {
      setSvgFocusId(null);
      // setFocusXYZ(null);
      // Clear external location state.
      setExternalLocations([]);
      setIsExternalFocus(false);
      if (externalMarker) {
        externalMarker.setMap(null);
        setExternalMarker(null);
      }
      externalMarkers.forEach((m) => m.setMap(null));
      setExternalMarkers([]);
      routeRenderers.forEach((r) => r.setMap(null));
      setRouteRenderers([]);
      travelTimeOverlays.forEach((t) => t.setMap(null));
      setTravelTimeOverlays([]);
      if (map && commBounds && !isSiteSeeOrPib) {
        // Interactive Map: fit to bounds
        fitAndPanToCommBounds();
      } else {
        // SiteSee/PibPage or no bounds: use initial zoom and center
        map.setZoom(initialZoom);
        map.panTo(initialCenterLatLng);
      }
      // Reset flag.
      setResetMap(false);
    }
  }, [resetMap,
    map,
    commBounds,
    initialZoom,
    initialCenterLatLng,
    isSiteSeeOrPib]);

  // Set map zoom from other components.
  useEffect(() => {
    if (map && activeZoom) {
      const currentZoom = map.getZoom();
      if (currentZoom !== activeZoom) {
        isProgrammaticZoomRef.current = true;
        map.setZoom(activeZoom);
      }
    }
  }, [map,
    activeZoom]);

  // Provide active map zoom to other components with mount checks.
  useEffect(() => {
    if (!isMountedRef.current) return;

    let zoomListener = null;
    if (map && isMountedRef.current) {
      zoomListener = map.addListener('zoom_changed', () => {
        const newZoom = map.getZoom();
        setActiveZoom(newZoom);
        // Set map idle to false when zoom changes (animation starting)
        if (setMapIdle) {
          setMapIdle(false);
        }
        // Set isZooming to true when zoom changes
        if (setIsZooming) {
          setIsZooming(true);
        }
        // Only clear focus when user manually zooms, not on programmatic zoom
        if (!isProgrammaticZoomRef.current) {
          if (setFocusLatLng && isMountedRef.current) {
            setFocusLatLng(null);
          }
          if (setFocusXYZ && isMountedRef.current) {
            setFocusXYZ(null);
          }
        }
        // Reset the programmatic zoom flag
        isProgrammaticZoomRef.current = false;
      });
    }

    // Cleanup listener on unmount or map change
    return () => {
      if (zoomListener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(zoomListener);
      }
    };
  }, [map,
    setMapIdle,
    setIsZooming,
    setFocusLatLng,
    setFocusXYZ]);

  // Clear focus when user pans the map manually
  useEffect(() => {
    if (!isMountedRef.current) return;

    let dragListener = null;

    if (map && isMountedRef.current) {
      // When user starts panning, clear focus
      dragListener = map.addListener('dragstart', () => {
        if (setFocusLatLng && isMountedRef.current) {
          setFocusLatLng(null);
        }
        if (setFocusXYZ && isMountedRef.current) {
          setFocusXYZ(null);
        }
      });
    }

    // Cleanup listener on unmount or map change
    return () => {
      if (dragListener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(dragListener);
      }
    };
  }, [map,
    setFocusLatLng,
    setFocusXYZ]);

  // Track when map becomes idle (after zoom animations complete)
  useEffect(() => {
    if (!isMountedRef.current) return;

    let idleListener = null;

    if (map && isMountedRef.current) {
      // Set map idle to true when map becomes idle (after zoom complete)
      idleListener = map.addListener('idle', () => {
        if (setMapIdle && isMountedRef.current) {
          setMapIdle(true);
        }
        // Clear isZooming when map becomes idle
        if (setIsZooming && isMountedRef.current) {
          setIsZooming(false);
        }
      });
    }

    // Cleanup listener on unmount or map change
    return () => {
      if (idleListener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(idleListener);
      }
    };
  }, [map,
    setMapIdle,
    setIsZooming]);

  // Detect effective max zoom using a test map with the SAME styled map type.
  // This accurately detects where styled tiles stop being available.
  // Cached in localStorage to avoid instantiating a second billed map on every mount.
  const hasDetectedMaxZoomRef = useRef(false);
  const testCenter = siteSeeCenter || (campusBounds ? {
    lat: (campusBounds.north + campusBounds.south) / 2,
    lng: (campusBounds.east + campusBounds.west) / 2,
  } : null);

  useEffect(() => {
    if (!testCenter || !maxZoom || hasDetectedMaxZoomRef.current || !window.google?.maps?.Map) return undefined;

    // Cache key: rounded coords + maxZoom + style. ~100m granularity per community,
    // and a config-driven maxZoom change re-probes.
    const cacheKey = `yt-effective-max-zoom:${testCenter.lat.toFixed(3)}:${testCenter.lng.toFixed(3)}:${maxZoom}:ROADMAP_POI_OFF`;
    const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

    try {
      const raw = window?.localStorage?.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed
          && typeof parsed.value === 'number'
          && typeof parsed.timestamp === 'number'
          && Date.now() - parsed.timestamp < CACHE_TTL_MS
        ) {
          setEffectiveMaxZoom(parsed.value);
          hasDetectedMaxZoomRef.current = true;
          return undefined;
        }
      }
    } catch (e) {
      // Ignore cache read errors and fall through to live probe.
    }

    // Create test div
    const testDiv = document.createElement('div');
    testDiv.style.cssText = 'position:fixed;bottom:0;right:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1;';
    document.body.appendChild(testDiv);

    // Create test map with same config
    const testMapOptions = {
      center: testCenter,
      zoom: maxZoom,
      minZoom,
      maxZoom,
      disableDefaultUI: true,
    };
    if (maxZoomBounds) {
      testMapOptions.restriction = { latLngBounds: maxZoomBounds, strictBounds: false };
    }

    const testMap = new window.google.maps.Map(testDiv, testMapOptions);

    // Apply the same map style as the real map
    applyMapStyle(testMap, 'ROADMAP_POI_OFF');

    // Wait for idle, then try incrementing zoom to find where it bounces
    let detectedMax = maxZoom;
    let checkCount = 0;

    const checkZoom = () => {
      const actualZoom = testMap.getZoom();

      // If zoom is less than what we requested, we found the limit
      if (actualZoom < detectedMax) {
        detectedMax = actualZoom;
      }

      checkCount++;
      // After a few checks, if zoom stabilizes, use that as effective max
      if (checkCount >= 3) {
        if (isMountedRef.current) {
          setEffectiveMaxZoom(detectedMax);
          hasDetectedMaxZoomRef.current = true;
        }
        try {
          window?.localStorage?.setItem(
            cacheKey,
            JSON.stringify({ value: detectedMax, timestamp: Date.now() }),
          );
        } catch (e) {
          // Ignore cache write errors.
        }
        testDiv.remove();
        return;
      }

      // Try to zoom higher
      testMap.setZoom(actualZoom + 1);
      const listener = testMap.addListener('idle', () => {
        window.google.maps.event.removeListener(listener);
        checkZoom();
      });
    };

    const initialListener = testMap.addListener('idle', () => {
      window.google.maps.event.removeListener(initialListener);
      checkZoom();
    });

    return () => {
      if (testDiv.parentNode) testDiv.remove();
    };
  }, [testCenter, minZoom, maxZoom, maxZoomBounds, setEffectiveMaxZoom]);

  // Derive target zoom from focus XYZ.
  const { z: focusZ } = focusXYZ || {};

  // If zoom changes while we have a focus XYZ, update the Z.
  // Only update z — don't re-derive focusLatLng since x/y haven't changed.
  // Skip during animation (focusAnimationComplete === false) so the target zoom
  // isn't overwritten by intermediate activeZoom values from zoom steps.
  useEffect(() => {
    if (map && focusXYZ && activeZoom && focusAnimationComplete) {
      const { z } = focusXYZ || {};
      if (z !== activeZoom) {
        setFocusXYZ({ ...focusXYZ, z: activeZoom });
      }
    }
  }, [activeZoom]);

  // Set focus latlng from focus XYZ.
  // Only re-derive when x/y change (new focus target), not when z changes
  // (zoom level update during animation).
  const focusX = focusXYZ?.x;
  const focusY = focusXYZ?.y;
  useEffect(() => {
    if (focusXYZ && svgData && overlayBounds && map) {
      const location = getLatLngFromFocusXYZ(focusXYZ, svgData, overlayBounds);
      setFocusLatLng(location);
    }
  }, [focusX, focusY, svgData, overlayBounds, map]);

  // Pan then zoom when focusLatLng is set.
  // For pending residence focus (initial page load), set center + zoom instantly.
  // For external locations, fit bounds to show both community pin and external location.
  // Otherwise, animate with deliberate timing: pan first, then zoom incrementally.
  useEffect(() => {
    if (map && focusLatLng) {
      if (hasPendingResidenceFocus) {
        const targetZoom = focusZ;
        if (targetZoom) setActiveZoom(targetZoom);
        map.setCenter(focusLatLng);
        hasInitialFocusRef.current = true;
      } else if (isExternalFocus && initialCenterLatLng) {
        setFocusAnimationComplete(false);
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(initialCenterLatLng);
        // Fit all external locations into bounds.
        const locs = externalLocations.length > 0 ? externalLocations : [{ lat: focusLatLng.lat(), lng: focusLatLng.lng() }];
        locs.forEach((loc) => {
          bounds.extend(new window.google.maps.LatLng(loc.lat, loc.lng));
        });
        map.fitBounds(bounds, 80);
        setFocusAnimationComplete(true);
      } else {
        const targetZoom = focusZ;
        setFocusAnimationComplete(false);
        const cancelAnimation = animateMapNavigation(map, focusLatLng, targetZoom, {
          onZoomStep: () => {
            isProgrammaticZoomRef.current = true;
          },
          onComplete: (finalZoom) => {
            if (finalZoom) setActiveZoom(finalZoom);
            setFocusAnimationComplete(true);
          },
        });
        return cancelAnimation;
      }
    }
    return undefined;
  }, [map, focusLatLng, hasPendingResidenceFocus, isExternalFocus, initialCenterLatLng]);

  // Lift or restore map restriction for external location focus.
  useEffect(() => {
    if (!map) return;
    if (isExternalFocus) {
      map.setOptions({ restriction: null });
    } else if (maxZoomBounds) {
      map.setOptions({
        restriction: {
          latLngBounds: maxZoomBounds,
          strictBounds: false,
        },
      });
    }
  }, [map, isExternalFocus, maxZoomBounds]);

  // Create/remove label overlays for external locations.
  useEffect(() => {
    if (!map) return undefined;

    // Clean up previous overlays.
    if (externalMarker) {
      externalMarker.setMap(null);
      setExternalMarker(null);
    }
    externalMarkers.forEach((m) => m.setMap(null));
    setExternalMarkers([]);

    const locations = externalLocations.slice(0, 3);

    if (locations.length > 0 && isExternalFocus) {
      const newMarkers = locations.map((loc, index) => {
        const tint = LOCATION_TINTS[index % LOCATION_TINTS.length];
        const overlay = new window.google.maps.OverlayView();

        overlay.onAdd = function onAdd() {
          const container = document.createElement('div');
          container.className = 'externalLocationLabel';

          const dot = document.createElement('div');
          dot.className = 'externalLocationLabel__dot';
          dot.style.background = tint;
          container.appendChild(dot);

          const label = document.createElement('div');
          label.className = 'externalLocationLabel__label';
          label.style.background = tint;

          const text = document.createElement('span');
          text.className = 'externalLocationLabel__text';
          text.textContent = loc.title;
          label.appendChild(text);

          container.appendChild(label);
          this.container = container;
          this.labelEl = label;
          this.getPanes().floatPane.appendChild(container);
        };

        overlay.draw = function draw() {
          const projection = this.getProjection();
          const position = projection.fromLatLngToDivPixel(
            new window.google.maps.LatLng(loc.lat, loc.lng),
          );
          if (position && this.container) {
            this.container.style.left = `${position.x}px`;
            this.container.style.top = `${position.y}px`;
          }
        };

        overlay.onRemove = function onRemove() {
          if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
          }
        };

        overlay.setMap(map);
        return overlay;
      });

      setExternalMarkers(newMarkers);
      // Keep backward compat for single marker ref.
      if (newMarkers.length > 0) setExternalMarker(newMarkers[0]);

      return () => {
        newMarkers.forEach((m) => m.setMap(null));
      };
    }
    return undefined;
  }, [map, externalLocations, isExternalFocus]);

  // Create/remove community logo overlay when external focus is active.
  const communityLogoOverlayRef = useRef(null);
  useEffect(() => {
    if (!map) return undefined;

    // Clean up previous overlay.
    if (communityLogoOverlayRef.current) {
      communityLogoOverlayRef.current.setMap(null);
      communityLogoOverlayRef.current = null;
    }

    if (isExternalFocus && initialCenterLatLng && config?.communityLogo) {
      const overlay = new window.google.maps.OverlayView();

      overlay.onAdd = function onAdd() {
        const container = document.createElement('div');
        container.className = 'communityLocationLabel';

        const icon = document.createElement('div');
        icon.className = 'communityLocationLabel__icon';
        const bg = document.createElement('div');
        bg.className = 'communityLocationLabel__icon__bg';
        const doughnut = document.createElement('div');
        doughnut.className = 'communityLocationLabel__icon__doughnut';
        const plus = document.createElement('div');
        plus.className = 'communityLocationLabel__icon__plus';
        icon.appendChild(bg);
        icon.appendChild(doughnut);
        icon.appendChild(plus);
        container.appendChild(icon);

        const label = document.createElement('div');
        label.className = 'communityLocationLabel__label';

        const img = document.createElement('img');
        img.className = 'communityLocationLabel__logo';
        img.src = config.communityLogo;
        img.alt = config.communityName || 'Community';
        label.appendChild(img);

        container.appendChild(label);
        container.style.cursor = 'pointer';
        container.title = 'Return to community';
        container.addEventListener('click', () => {
          setResetMap(true);
        });
        this.container = container;
        this.getPanes().floatPane.appendChild(container);
      };

      overlay.draw = function draw() {
        const projection = this.getProjection();
        const position = projection.fromLatLngToDivPixel(initialCenterLatLng);
        if (position && this.container) {
          this.container.style.left = `${position.x}px`;
          this.container.style.top = `${position.y}px`;
        }
      };

      overlay.onRemove = function onRemove() {
        if (this.container && this.container.parentNode) {
          this.container.parentNode.removeChild(this.container);
        }
      };

      overlay.setMap(map);
      communityLogoOverlayRef.current = overlay;

      return () => {
        overlay.setMap(null);
      };
    }
    return undefined;
  }, [map, isExternalFocus, initialCenterLatLng, config]);

  // Render driving routes between community and external locations,
  // and append travel time into the location label overlays.
  useEffect(() => {
    if (!map) return undefined;

    // Clean up previous routes.
    if (routeRenderer) {
      routeRenderer.setMap(null);
      setRouteRenderer(null);
    }
    routeRenderers.forEach((r) => r.setMap(null));
    setRouteRenderers([]);

    const locations = externalLocations.slice(0, 3);

    if (!isExternalFocus || locations.length === 0 || !initialCenterLatLng) return undefined;

    const directionsService = new window.google.maps.DirectionsService();
    const newRenderers = [];
    let routesResolved = 0;

    locations.forEach((loc, index) => {
      const tint = LOCATION_TINTS[index % LOCATION_TINTS.length];

      const renderer = new window.google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: {
          strokeColor: tint,
          strokeOpacity: 0.8,
          strokeWeight: 4,
        },
      });

      directionsService.route(
        {
          origin: initialCenterLatLng,
          destination: new window.google.maps.LatLng(loc.lat, loc.lng),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          routesResolved++;

          if (status === window.google.maps.DirectionsStatus.OK) {
            renderer.setDirections(result);

            const leg = result.routes[0]?.legs[0];
            if (leg) {
              const durationText = leg.duration.text;

              // Append travel time to the corresponding label overlay.
              const markerOverlay = externalMarkers[index];
              if (markerOverlay?.labelEl) {
                const timeEl = document.createElement('span');
                timeEl.className = 'externalLocationLabel__time';
                timeEl.textContent = durationText;
                markerOverlay.labelEl.appendChild(timeEl);
              }
            }
          }

          // Re-resolve label overlaps after all routes have responded
          // (labels now include travel time text and have their final size).
          if (routesResolved === locations.length) {
            requestAnimationFrame(() => {
              resolveExternalLabelOverlaps(externalMarkers);
            });
          }
        },
      );

      newRenderers.push(renderer);
    });

    setRouteRenderers(newRenderers);
    // Backward compat.
    if (newRenderers.length > 0) setRouteRenderer(newRenderers[0]);

    return () => {
      newRenderers.forEach((r) => r.setMap(null));
    };
  }, [map, externalLocations, isExternalFocus, initialCenterLatLng, externalMarkers]);

  // Re-resolve external label overlaps after pan/zoom — labels are tethered
  // to their dots, so their relative positions shift as the map moves.
  useEffect(() => {
    if (!map || externalMarkers.length < 2) return undefined;

    const listener = map.addListener('idle', () => {
      requestAnimationFrame(() => {
        resolveExternalLabelOverlaps(externalMarkers);
      });
    });

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [map, externalMarkers]);

  return (
    <div
      className="mapShell"
      ref={googleMapContainer}
      tabIndex="-1"
      // Use inert attribute during initialization to prevent Google Maps from grabbing focus
      // and causing unwanted scroll behavior. Remove once map is ready for interaction.
      {...(!isMapReady && { inert: '' })}
    >
      <GoogleMapOverlayViewContainer />
    </div>
  );
});

GoogleMap.displayName = 'GoogleMap';

export default GoogleMap;
